/**
 * The Talbinah backend wraps (almost) every response in a status envelope:
 *
 *   `{ status: boolean, message: string | null, data: T }`
 *
 * A few endpoints (e.g. `GET /api/v3/subscription/plans`) return a bare payload,
 * and Laravel validation errors (422) return `{ message, errors }` — neither is
 * an envelope. {@link unwrapEnvelope} tolerates all three: it returns `data` when
 * an envelope is present and the value as-is otherwise.
 */
export interface ApiEnvelope<T> {
  status: boolean;
  message: string | null;
  data: T;
}

/** Type-guard: is `res` a `{ status, message, data }` envelope? */
export function isApiEnvelope<T>(res: unknown): res is ApiEnvelope<T> {
  return (
    res !== null &&
    typeof res === 'object' &&
    'data' in (res as Record<string, unknown>) &&
    'status' in (res as Record<string, unknown>)
  );
}

/** Return `res.data` when `res` is an envelope, else `res` untouched. */
export function unwrapEnvelope<T>(res: ApiEnvelope<T> | T): T {
  return res !== null && typeof res === 'object' && 'data' in (res as Record<string, unknown>)
    ? (res as ApiEnvelope<T>).data
    : (res as T);
}

/** A success payload together with the envelope's own `message` (null when bare / absent). */
export interface ApiResult<T> {
  data: T;
  message: string | null;
}

/** Like {@link unwrapEnvelope}, but keep the envelope's success `message` alongside `data`. */
export function unwrapWithMessage<T>(res: ApiEnvelope<T> | T): ApiResult<T> {
  const message = isApiEnvelope<T>(res) && typeof res.message === 'string' ? res.message : null;
  return { data: unwrapEnvelope<T>(res), message };
}
